import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Headphones, Loader2, Link2, Twitter, Linkedin, MessageSquare, Share2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";

export default function PodcastPublic() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [ep, setEp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/podcast/${id}`)
      .then(({ data }) => setEp(data))
      .catch(() => setEp(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!ep) return;
    const prev = document.title;
    document.title = `${ep.title} · COCOON`;
    return () => { document.title = prev; };
  }, [ep]);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareText = ep ? `${ep.title} · COCOON` : "COCOON";
  const enc = encodeURIComponent;
  const twitter = `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(url)}`;
  const whatsapp = `https://wa.me/?text=${enc(shareText + " " + url)}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    toast.success(t("echo.linkCopied"));
  };

  return (
    <div className="min-h-screen bg-background text-foreground starfield">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="podcastpublic-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/entrer" data-testid="podcastpublic-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-32 pb-24">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary/60" size={30} /></div>
        ) : !ep ? (
          <div className="glass rounded-2xl p-12 text-center mt-10">
            <p className="font-display italic text-foreground/50 text-2xl">{t("podcastPublic.notFound")}</p>
            <Link to="/" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm mt-8">{t("podcastPublic.cta")}</Link>
          </div>
        ) : (
          <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} data-testid="podcastpublic-article">
            {/* generated orange cover card */}
            <div
              className="relative rounded-3xl overflow-hidden p-10 mb-10 frame-pulse"
              style={{ background: "linear-gradient(135deg, #7c2d12 0%, #ea580c 45%, #f59e0b 100%)" }}
              data-testid="podcastpublic-cover"
            >
              <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.5), transparent 45%)" }} />
              <div className="relative">
                <div className="flex items-center gap-2 text-white/80 uppercase tracking-[0.22em] text-xs mb-6">
                  <Headphones size={16} /> {t("podcastPublic.eyebrow")}
                </div>
                <h1 className="font-display text-white text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-4" data-testid="podcastpublic-title">{ep.title}</h1>
                <p className="text-white/85 italic font-display text-lg">{t("podcastPublic.intro")}</p>
                <div className="flex items-center gap-2 text-white/70 text-sm mt-8">
                  <Sparkles size={15} /> {t("podcastPublic.by")}
                </div>
              </div>
            </div>

            <p className="text-foreground/75 leading-relaxed text-lg whitespace-pre-line mb-12" data-testid="podcastpublic-script">{ep.script}</p>

            <Link to="/entrer" data-testid="podcastpublic-cta" className="btn-frost-orange inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-base mb-14">
              <Sparkles size={18} /> {t("podcastPublic.cta")}
            </Link>

            <div className="border-t border-white/10 pt-8">
              <p className="uppercase tracking-[0.22em] text-xs text-foreground/40 mb-4 flex items-center gap-2">
                <Share2 size={14} /> {t("echo.share")}
              </p>
              <div className="flex flex-wrap gap-3">
                <button onClick={copyLink} data-testid="pp-share-copy" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                  <Link2 size={15} /> {t("echo.copyLink")}
                </button>
                <a href={twitter} target="_blank" rel="noopener noreferrer" data-testid="pp-share-twitter" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                  <Twitter size={15} /> X
                </a>
                <a href={whatsapp} target="_blank" rel="noopener noreferrer" data-testid="pp-share-whatsapp" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                  <MessageSquare size={15} /> WhatsApp
                </a>
                <a href={linkedin} target="_blank" rel="noopener noreferrer" data-testid="pp-share-linkedin" className="glass rounded-full px-4 py-2.5 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                  <Linkedin size={15} /> LinkedIn
                </a>
              </div>
            </div>
          </motion.article>
        )}
      </section>
    </div>
  );
}
